import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const SignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signUp, signInWithGoogle } = useAuth();

  const inviteToken = searchParams?.get('token') || '';
  const inviteEmail = searchParams?.get('email') || '';
  const isInvited = !!inviteToken;

  const [step, setStep] = useState(isInvited ? 2 : 1);
  const [formData, setFormData] = useState({
    orgName: '',
    fullName: '',
    email: inviteEmail || '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');

  useEffect(() => {
    if (inviteEmail) {
      setFormData(prev => ({ ...prev, email: inviteEmail }));
    }
  }, [inviteEmail]);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    setGeneralError('');
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData?.orgName?.trim()) newErrors.orgName = 'Organization name is required';
    if (!formData?.fullName?.trim()) newErrors.fullName = 'Your name is required';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData?.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/?.test(formData?.email)) newErrors.email = 'Invalid email address';
    if (!formData?.fullName?.trim() && isInvited) newErrors.fullName = 'Your name is required';
    if (!formData?.password) newErrors.password = 'Password is required';
    else if (formData?.password?.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (formData?.password !== formData?.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) setStep(2);
  };

  const handleGoogleSignUp = async () => {
    setIsGoogleLoading(true);
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        setGeneralError(error?.message || 'Google sign-up failed. Please try again.');
        setIsGoogleLoading(false);
      }
    } catch (err) {
      setGeneralError('Google sign-up failed. Please try again.');
      setIsGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateStep2()) return;
    setIsLoading(true);
    setGeneralError('');
    try {
      const { error } = await signUp(
        formData?.email,
        formData?.password,
        formData?.fullName || '',
        isInvited ? null : formData?.orgName,
        isInvited ? inviteToken : null
      );
      if (error) {
        setGeneralError(error?.message);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setGeneralError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{isInvited ? 'Accept Invitation' : 'Create Account'} - Wilcart CRM</title>
        <meta name="description" content="Create your Wilcart CRM workspace and start managing your business relationships." />
      </Helmet>
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 pointer-events-none" />

        {/* Decorative blobs */}
        <div className="absolute top-10 left-10 w-32 h-32 bg-primary/10 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-secondary/10 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute top-1/2 right-20 w-24 h-24 bg-primary/5 rounded-full blur-xl pointer-events-none" />

        <div className="relative w-full max-w-md">
          <div className="bg-card border border-border rounded-2xl shadow-elevation-2 p-6 sm:p-8">

            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-6">
                <img
                  src="/assets/images/wilcart_logo_1-1775846328647.png"
                  alt="Wilcart CRM Logo"
                  className="h-14 w-auto object-contain"
                />
                <span className="ml-3 text-2xl font-bold text-foreground">Wilcart</span>
              </div>
              {isInvited ? (
                <>
                  <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-3">
                    <Icon name="Mail" size={14} />
                    <span>Team Invitation</span>
                  </div>
                  <h2 className="text-xl font-semibold text-foreground">Accept your invitation</h2>
                  <p className="text-muted-foreground mt-1 text-sm">Create your account to join the team</p>
                </>
              ) : (
                <>
                  <h2 className="text-xl font-semibold text-foreground">Create your workspace</h2>
                  <p className="text-muted-foreground mt-1 text-sm">Set up your organization and start managing your CRM</p>
                </>
              )}
            </div>

            {/* Step Indicator (only for non-invite flow) */}
            {!isInvited && (
              <div className="flex items-center justify-center space-x-4 mb-8">
                <div className={`flex items-center space-x-2 ${step >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                    step > 1 ? 'bg-primary text-primary-foreground' : step === 1 ? 'bg-primary text-primary-foreground ring-4 ring-primary/20' : 'bg-muted text-muted-foreground'
                  }`}>
                    {step > 1 ? <Icon name="Check" size={14} /> : '1'}
                  </div>
                  <span className="text-sm font-medium">Organization</span>
                </div>
                <div className={`flex-1 h-px transition-all ${step >= 2 ? 'bg-primary' : 'bg-border'}`} />
                <div className={`flex items-center space-x-2 ${step >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                    step >= 2 ? 'bg-primary text-primary-foreground ring-4 ring-primary/20' : 'bg-muted text-muted-foreground'
                  }`}>2</div>
                  <span className="text-sm font-medium">Account</span>
                </div>
              </div>
            )}

            {generalError && (
              <div className="mb-5 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-400">{generalError}</p>
                </div>
              </div>
            )}

            {/* Step 1: Organization (only for new workspace) */}
            {!isInvited && step === 1 && (
              <div className="space-y-5">
                {/* Google OAuth */}
                <button
                  type="button"
                  onClick={handleGoogleSignUp}
                  disabled={isGoogleLoading}
                  className="w-full flex items-center justify-center space-x-3 px-4 py-3 bg-white dark:bg-card border border-border rounded-xl text-sm font-medium text-foreground hover:bg-muted/50 transition-all duration-200 shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGoogleLoading ? (
                    <div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                  )}
                  <span>{isGoogleLoading ? 'Redirecting...' : 'Continue with Google'}</span>
                </button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-3 text-muted-foreground font-medium tracking-wider">or create with email</span>
                  </div>
                </div>

                <Input
                  label="Organization Name"
                  type="text"
                  name="orgName"
                  placeholder="e.g. Acme Corp"
                  value={formData?.orgName}
                  onChange={handleChange}
                  error={errors?.orgName}
                  required
                />
                <Input
                  label="Your Full Name"
                  type="text"
                  name="fullName"
                  placeholder="e.g. John Smith"
                  value={formData?.fullName}
                  onChange={handleChange}
                  error={errors?.fullName}
                  required
                />
                <Button
                  type="button"
                  variant="default"
                  size="lg"
                  fullWidth
                  onClick={handleNext}
                  className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 shadow-lg shadow-primary/20"
                >
                  Continue
                  <Icon name="ArrowRight" size={16} className="ml-2" />
                </Button>
              </div>
            )}

            {/* Step 2: Account credentials */}
            {(step === 2 || isInvited) && (
              <form onSubmit={handleSubmit} className="space-y-4">
                {isInvited && (
                  <Input
                    label="Your Full Name"
                    type="text"
                    name="fullName"
                    placeholder="e.g. John Smith"
                    value={formData?.fullName}
                    onChange={handleChange}
                    error={errors?.fullName}
                    required
                    disabled={isLoading}
                  />
                )}
                <Input
                  label="Email Address"
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  value={formData?.email}
                  onChange={handleChange}
                  error={errors?.email}
                  required
                  disabled={isLoading || !!inviteEmail}
                />
                {inviteEmail && (
                  <p className="text-xs text-muted-foreground -mt-2 flex items-center space-x-1">
                    <Icon name="Lock" size={12} />
                    <span>Email is pre-filled from your invitation</span>
                  </p>
                )}
                <Input
                  label="Password"
                  type="password"
                  name="password"
                  placeholder="At least 8 characters"
                  value={formData?.password}
                  onChange={handleChange}
                  error={errors?.password}
                  required
                  disabled={isLoading}
                />
                <Input
                  label="Confirm Password"
                  type="password"
                  name="confirmPassword"
                  placeholder="Repeat your password"
                  value={formData?.confirmPassword}
                  onChange={handleChange}
                  error={errors?.confirmPassword}
                  required
                  disabled={isLoading}
                />

                {/* Password strength hint */}
                {formData?.password?.length > 0 && (
                  <div className="flex items-center space-x-2">
                    {[1,2,3,4]?.map(i => (
                      <div key={i} className={`h-1 flex-1 rounded-full transition-all ${
                        formData?.password?.length >= i * 3
                          ? i <= 2 ? 'bg-yellow-400' : i === 3 ? 'bg-blue-400' : 'bg-green-400' :'bg-muted'
                      }`} />
                    ))}
                    <span className="text-xs text-muted-foreground ml-1">
                      {formData?.password?.length < 6 ? 'Weak' : formData?.password?.length < 10 ? 'Fair' : formData?.password?.length < 12 ? 'Good' : 'Strong'}
                    </span>
                  </div>
                )}

                <div className="flex space-x-3 pt-1">
                  {!isInvited && (
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      onClick={() => setStep(1)}
                      disabled={isLoading}
                      className="flex-1"
                    >
                      Back
                    </Button>
                  )}
                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    loading={isLoading}
                    disabled={isLoading}
                    className={`${isInvited ? 'w-full' : 'flex-1'} bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 shadow-lg shadow-primary/20`}
                  >
                    {isLoading ? 'Creating...' : isInvited ? 'Join Team' : 'Create Workspace'}
                  </Button>
                </div>

                <p className="text-xs text-center text-muted-foreground pt-1">
                  By creating an account, you agree to our{' '}
                  <span className="text-primary cursor-pointer hover:underline">Terms of Service</span>
                  {' '}and{' '}
                  <span className="text-primary cursor-pointer hover:underline">Privacy Policy</span>
                </p>
              </form>
            )}

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:text-primary/80 font-semibold">
                  Sign in
                </Link>
              </p>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-muted-foreground">© {new Date()?.getFullYear()} Wilcart. All rights reserved.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignupPage;
